import React from 'react';

const defaultContainerStyle = {
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '24px',
  background: '#f5f7fb'
};

const defaultCardStyle = {
  width: '100%',
  maxWidth: '560px',
  background: '#fff',
  border: '1px solid #e6e9f0',
  borderRadius: '12px',
  padding: '24px',
  boxShadow: '0 10px 30px rgba(9, 20, 54, 0.08)',
  textAlign: 'center'
};

const defaultButtonStyle = {
  border: '1px solid #cfd6e6',
  background: '#fff',
  color: '#243b64',
  borderRadius: '8px',
  height: '40px',
  padding: '0 16px',
  cursor: 'pointer',
  fontWeight: 600
};

const primaryButtonStyle = {
  ...defaultButtonStyle,
  background: '#1f4fbf',
  borderColor: '#1f4fbf',
  color: '#fff'
};

const DefaultFallback = ({ onRetry, onGoHome, onReload }) => (
  <div style={defaultContainerStyle} role="alert" aria-live="assertive">
    <div style={defaultCardStyle}>
      <h2 style={{ margin: 0, color: '#122746' }}>Something went wrong</h2>
      <p style={{ margin: '10px 0 0', color: '#3f5374' }}>
        An unexpected error occurred. Please try again.
      </p>
      <div
        style={{
          marginTop: '18px',
          display: 'flex',
          gap: '10px',
          justifyContent: 'center',
          flexWrap: 'wrap'
        }}
      >
        <button type="button" style={primaryButtonStyle} onClick={onRetry}>
          Retry
        </button>
        <button type="button" style={defaultButtonStyle} onClick={onGoHome}>
          Go to home
        </button>
        <button type="button" style={defaultButtonStyle} onClick={onReload}>
          Reload page
        </button>
      </div>
    </div>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null
    };
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error, errorInfo) {
    // Keep this lightweight for phase 1; observability hooks can be added later.
    if (process.env.NODE_ENV !== 'test') {
      console.error('[ErrorBoundary] Unhandled render error:', error, errorInfo);
    }
  }

  componentDidUpdate(prevProps) {
    const { resetKey } = this.props;
    if (this.state.hasError && prevProps.resetKey !== resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.assign('/app');
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    const { hasError } = this.state;
    const { children, fallback } = this.props;

    if (!hasError) {
      return children;
    }

    if (fallback) {
      if (React.isValidElement(fallback)) {
        return fallback;
      }

      if (typeof fallback === 'function') {
        return fallback({
          onRetry: this.handleRetry,
          onGoHome: this.handleGoHome,
          onReload: this.handleReload
        });
      }
    }

    return (
      <DefaultFallback
        onRetry={this.handleRetry}
        onGoHome={this.handleGoHome}
        onReload={this.handleReload}
      />
    );
  }
}

export default ErrorBoundary;
